import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { publicAPI } from '../../lib/api';
import { Building2, MapPin, Phone, Mail, Globe, ArrowRight, Factory } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Breadcrumb, breadcrumbJsonLd } from '../../components/Breadcrumb';

export default function DirectoryCompanyPage() {
  const { categorySlug, companySlug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    publicAPI.getDirectoryCompany(companySlug)
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [companySlug]);

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[hsl(221,83%,22%)]"></div></div>;
  if (!data) return <div className="min-h-screen flex flex-col items-center justify-center"><h1 className="text-2xl font-semibold mb-4">Firma bulunamadi</h1><Link to="/sanayi-rehberi" className="text-[hsl(221,83%,22%)] underline">Rehbere don</Link></div>;

  const { company, category } = data;

  const companyBreadcrumb = [
    { label: 'Ana Sayfa', href: '/' },
    { label: 'Sanayi Rehberi', href: '/sanayi-rehberi' },
    ...(category ? [{ label: category.name, href: `/sanayi-rehberi/${category.slug}` }] : []),
    { label: company.name, href: `/sanayi-rehberi/${categorySlug}/${companySlug}` }
  ];

  return (
    <>
      <Helmet>
        <title>{company.meta_title || company.name ? `${company.meta_title || company.name} | Sanayi Rehberi` : 'Sanayi Rehberi'}</title>
        <meta name="description" content={company.meta_description || company.description || ''} />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          "name": company.name,
          "description": company.description,
          "address": { "@type": "PostalAddress", "addressLocality": company.city, "addressCountry": "TR" },
          ...(company.phone ? { "telephone": company.phone } : {}),
          ...(company.email ? { "email": company.email } : {}),
          ...(company.website ? { "url": company.website } : {})
        })}</script>
        <script type="application/ld+json">{breadcrumbJsonLd(companyBreadcrumb)}</script>
      </Helmet>

      <section className="bg-slate-50 py-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-4">
            <Breadcrumb items={companyBreadcrumb} />
          </div>
        </div>
      </section>

      <section className="py-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Main Info */}
            <div className="lg:col-span-2">
              <div className="flex items-start gap-4 mb-6">
                {company.logo ? (
                  <img src={company.logo} alt={company.name} className="h-16 w-auto object-contain" />
                ) : (
                  <Building2 className="h-14 w-14 text-[hsl(221,83%,22%)]" />
                )}
                <div>
                  <h1 className="text-2xl sm:text-3xl font-semibold" data-testid="dir-company-title">{company.name}</h1>
                  {company.city && (
                    <div className="flex items-center gap-1 text-muted-foreground mt-1">
                      <MapPin className="h-4 w-4" /> {company.city}
                    </div>
                  )}
                </div>
              </div>

              {company.description && (
                <Card className="mb-6">
                  <CardHeader><CardTitle>Hakkinda</CardTitle></CardHeader>
                  <CardContent>
                    <p className="text-sm leading-relaxed whitespace-pre-line">{company.description}</p>
                  </CardContent>
                </Card>
              )}

              {company.services && company.services.length > 0 && (
                <Card className="mb-6">
                  <CardHeader><CardTitle>Hizmetler</CardTitle></CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-2">
                      {company.services.map((s, i) => (
                        <Badge key={i} variant="secondary">{s}</Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {company.gallery && company.gallery.length > 0 && (
                <Card className="mb-6">
                  <CardHeader><CardTitle>Galeri</CardTitle></CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {company.gallery.map((img, i) => (
                        <img key={i} src={img} alt={`${company.name} ${i + 1}`} className="rounded-lg h-32 w-full object-cover" loading="lazy" />
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              <Card>
                <CardHeader><CardTitle>Iletisim</CardTitle></CardHeader>
                <CardContent className="space-y-3">
                  {company.phone && (
                    <div className="flex items-center gap-3 text-sm">
                      <Phone className="h-4 w-4 text-muted-foreground" />
                      <span>{company.phone}</span>
                    </div>
                  )}
                  {company.email && (
                    <div className="flex items-center gap-3 text-sm">
                      <Mail className="h-4 w-4 text-muted-foreground" />
                      <a href={`mailto:${company.email}`} className="text-[hsl(221,83%,22%)] hover:underline">{company.email}</a>
                    </div>
                  )}
                  {company.website && (
                    <div className="flex items-center gap-3 text-sm">
                      <Globe className="h-4 w-4 text-muted-foreground" />
                      <a href={company.website} target="_blank" rel="noopener noreferrer" className="text-[hsl(221,83%,22%)] hover:underline">Web Sitesi</a>
                    </div>
                  )}
                  {company.industrial_zone && (
                    <div className="flex items-center gap-3 text-sm">
                      <Factory className="h-4 w-4 text-muted-foreground" />
                      <span>{company.industrial_zone}</span>
                    </div>
                  )}
                </CardContent>
              </Card>

              <Link to="/teklif-al">
                <Button className="w-full" size="lg" data-testid="dir-company-cta">
                  Teklif Al <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
